import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LOGO_URL, IMAGES } from "@/lib/constants";
import { GoogleButton } from "@/components/GoogleButton";
import { WalletLoginButton } from "@/components/wallet/WalletLoginButton";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [refCode, setRefCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get("ref") || localStorage.getItem("cl_ref") || "";
    if (r) { const c = r.trim().toUpperCase(); setRefCode(c); localStorage.setItem("cl_ref", c); }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(name, email, password, phone);
      navigate(`/check-email?email=${encodeURIComponent(email)}`);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <img src={LOGO_URL} alt="OnchainLyfe" className="h-8 mb-8" />
          <h1 className="font-display text-3xl font-bold">Join the network</h1>
          <p className="text-zinc-400 text-sm mt-1 mb-6">Create a free Reader account. Upgrade to Alpha anytime.</p>
          {err && <div data-testid="register-error" className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">{err}</div>}
          <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Name</label>
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full mb-4 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="Your name" />
          <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Email</label>
          <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="you@fund.com" />
          <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Phone number (optional)</label>
          <input data-testid="register-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
            className="w-full mb-4 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="+62 812 3456 7890" />
          <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Password</label>
          <input data-testid="register-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-1.5 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none" placeholder="••••••••" />
          <p className="text-[11px] text-zinc-500 mb-6">At least 8 characters with an uppercase, a lowercase and a number.</p>
          <label className="block text-xs font-mono-cl tracking-[0.14em] uppercase text-zinc-500 mb-1.5">Referral code (optional)</label>
          <input data-testid="register-referral" value={refCode}
            onChange={(e) => { const c = e.target.value.toUpperCase(); setRefCode(c); localStorage.setItem("cl_ref", c); }}
            className="w-full mb-6 rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm focus:border-[#ccff00]/50 focus:outline-none font-mono-cl" placeholder="e.g. AB12CD3" />
          <button data-testid="register-submit" disabled={busy} className="w-full py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm hover:brightness-110 transition-[filter] disabled:opacity-60">
            {busy ? "Creating…" : "Create account"}
          </button>
          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] font-mono-cl tracking-[0.2em] uppercase text-zinc-600">or</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <GoogleButton label="Sign up with Google" />
          <WalletLoginButton />
          <p className="text-center text-sm text-zinc-500 mt-6">
            Already a member? <Link to="/login" className="text-[#ccff00] hover:underline">Log in</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:block relative grain order-1 lg:order-2">
        <img src={IMAGES.neon2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        <div className="relative h-full flex items-end p-12">
          <h2 className="font-display text-4xl font-black leading-tight">Join the network behind the research.</h2>
        </div>
      </div>
    </div>
  );
}
